// FROZEN CONTRACT — shared formatters. Agents must not change behaviour here.
import type { Currency } from "./types";

/**
 * Indian grouping: ₹13,20,450 — NOT ₹1,320,450.
 * en-IN handles the lakh/crore grouping natively.
 */
export function formatInr(value: number, opts: { decimals?: number } = {}): string {
  const decimals = opts.decimals ?? 2;
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}

const LOCALE_BY_CURRENCY: Record<Currency, string> = {
  INR: "en-IN",
  GBP: "en-GB",
  USD: "en-US",
};

export function formatMoney(
  value: number,
  currency: Currency,
  opts: { decimals?: number } = {},
): string {
  const decimals = opts.decimals ?? 2;
  return new Intl.NumberFormat(LOCALE_BY_CURRENCY[currency], {
    style: "currency",
    currency,
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}

/**
 * "£12,340.00 · ₹13,20,450.00" for any non-INR holding.
 * Returns the INR string alone when the native currency is already INR.
 */
export function formatDualCurrency(
  valueNative: number,
  nativeCurrency: Currency,
  valueInr: number,
): string {
  if (nativeCurrency === "INR") return formatInr(valueInr);
  return `${formatMoney(valueNative, nativeCurrency)} · ${formatInr(valueInr)}`;
}

/** Compact Indian scale for hero numbers: ₹1.32 Cr, ₹13.20 L, ₹9,450 */
export function formatInrCompact(value: number): string {
  const abs = Math.abs(value);
  const sign = value < 0 ? "-" : "";
  if (abs >= 1_00_00_000) return `${sign}₹${(abs / 1_00_00_000).toFixed(2)} Cr`;
  if (abs >= 1_00_000) return `${sign}₹${(abs / 1_00_000).toFixed(2)} L`;
  return formatInr(value, { decimals: 0 });
}

/** Mutual fund units carry 4 decimals; never render a raw float. */
export function formatUnits(units: number): string {
  return new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
  }).format(units);
}

/** "+12.34%" / "-3.10%" — sign is always explicit. */
export function formatPercent(pct: number, decimals = 2): string {
  const sign = pct > 0 ? "+" : "";
  return `${sign}${pct.toFixed(decimals)}%`;
}

/**
 * Gain percentage that never returns NaN or Infinity.
 * A holding with zero cost basis (a pure grant, say) reports 0, not ∞.
 */
export function safeGainPct(gain: number, invested: number): number {
  if (!Number.isFinite(invested) || invested === 0) return 0;
  return (gain / invested) * 100;
}

/** 'as of 2 hours ago' for price staleness badges. */
export function formatRelativeAge(iso: string, now: Date = new Date()): string {
  const ms = now.getTime() - new Date(iso).getTime();
  const mins = Math.floor(ms / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}
